// State machine + React context for the bulk-add feature. Mounted in the /words
// layout (above the route) so a session survives navigation between destinations:
// its analysis keeps polling, and its completion can raise a toast + rail badge
// even when the bulk-add page itself is not on screen.
//
// Sessions are persisted server-side as drafts (see lib/bulk-add/README.md), so a
// review survives a device switch: the provider hydrates the user's drafts on
// mount and writes each change through with a debounced autosave. The reducer is
// pure; all side effects (starting/polling the job, autosave, re-analysis, writing
// words at submit) live in the callback methods and effects.
import { wordsApi } from '@/lib/api';
import type { WordPatch } from '@/lib/api';
import { NOYAU_ORDER, incrementAt } from '@/lib/noyaux';
import { notifyUnauthorized } from '@/lib/tahiti-client';
import type { AnalyzedWord } from '@/schemas/bulk';
import type { NoyauCode, WordTag } from '@/schemas/word';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  DraftClientError,
  DraftUnauthorizedError,
  analyzeBulk,
  deleteDraft,
  getBulkJob,
  listDrafts,
  putDraft,
} from './bulk-api';
import { detectConflicts, unionTags } from './conflicts';
import { normalizeTitle } from './parse';
import type { BulkSession, BulkWord, DbMatch } from './types';

/** Poll cadence for in-flight analysis jobs. */
const POLL_INTERVAL_MS = 300;
/** Debounce before an edit is flushed to the server. */
const SAVE_DEBOUNCE_MS = 1000;
/** Backoff before retrying a failed autosave. */
const SAVE_RETRY_MS = 3000;
/** Safety cap on re-analysis polling iterations (≈ POLL_INTERVAL_MS × this). */
const MAX_REFRESH_POLLS = 200;

// ── Resolution + summary value types ────────────────────────────────────────

/** A user's choice in the conflict dialog, applied by the reducer. */
export type BulkResolution =
  | { kind: 'mergeBatch'; keepId: string; dropId: string }
  | { kind: 'mergeDb'; wordId: string; dbId: string; dbTitle: string; dbTags: WordTag[] }
  | { kind: 'keepBoth'; key: string }
  | { kind: 'deleteOne'; wordId: string };

/** A choice applied to every currently unresolved conflict at once (the "apply to all" bar). */
export type GlobalResolutionKind = 'merge' | 'keepBoth' | 'deleteExtra';

/** Outcome of a submit, shown in the summary dialog. */
export type SubmitSummary = { created: number; merged: number; failed: number };

/** Autosave state for a session, surfaced by the review footer indicator. */
export type SaveStatus = 'saving' | 'saved' | 'error';

/** A completion toast raised when a session becomes ready off-screen. */
type Toast = { sessionId: string; name: string; noyau: NoyauCode | null };

// ── Reducer state + actions ──────────────────────────────────────────────────

type State = {
  sessions: BulkSession[];
  /** The session currently open in the page; null shows entry/input. */
  openId: string | null;
  /** Which non-session screen the page shows when no session is open. */
  screen: 'entry' | 'input';
  /** The session the user is actively watching analyze (suppresses its toast). */
  watchingId: string | null;
  toast: Toast | null;
};

type Action =
  | { type: 'hydrate'; sessions: BulkSession[] }
  | { type: 'startNew' }
  | { type: 'cancelInput' }
  | { type: 'createSession'; session: BulkSession }
  | { type: 'updateInputDraft'; id: string; text: string; noyau: NoyauCode | null }
  | { type: 'promoteToAnalyzing'; id: string; text: string; noyau: NoyauCode | null }
  | { type: 'setJobId'; id: string; jobId: string }
  | { type: 'open'; id: string }
  | { type: 'close' }
  | { type: 'beginReview'; id: string }
  | { type: 'progress'; id: string; processed: number; total: number }
  | { type: 'ready'; id: string; words: BulkWord[]; toast: boolean }
  | { type: 'failAnalyze'; id: string }
  | { type: 'refreshMatches'; id: string; matches: Map<string, DbMatch | null> }
  | { type: 'setTags'; id: string; wordId: string; tags: WordTag[] }
  | { type: 'toggleExclude'; id: string; wordId: string }
  | { type: 'resolve'; id: string; resolution: BulkResolution }
  | { type: 'resolveAll'; id: string; kind: GlobalResolutionKind }
  | { type: 'restoreSnapshot'; id: string; words: BulkWord[]; resolvedKeys: string[] }
  | { type: 'markFailed'; id: string; failedIds: string[] }
  | { type: 'remove'; id: string }
  | { type: 'setWatching'; id: string | null }
  | { type: 'dismissToast' };

/** Apply a word-list transform to one session, leaving the others untouched. */
function mapSessionWords(
  state: State,
  id: string,
  fn: (words: BulkWord[], session: BulkSession) => BulkWord[],
): State {
  return {
    ...state,
    sessions: state.sessions.map((s) => (s.id === id ? { ...s, words: fn(s.words, s) } : s)),
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'hydrate': {
      // Server is the source of truth on load; the reducer is the working copy
      // thereafter. Merge (don't replace): a session created locally before the
      // hydrate GET resolves (fast paste-then-analyze) must not be clobbered, so
      // keep local sessions and append only server drafts not already present.
      const existing = new Set(state.sessions.map((s) => s.id));
      const incoming = action.sessions.filter((s) => !existing.has(s.id));
      return { ...state, sessions: [...state.sessions, ...incoming] };
    }
    case 'startNew':
      return { ...state, screen: 'input', openId: null };
    case 'cancelInput':
      return { ...state, screen: 'entry' };
    case 'createSession':
      return { ...state, sessions: [action.session, ...state.sessions], openId: action.session.id };
    case 'updateInputDraft':
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.id === action.id ? { ...s, text: action.text, noyau: action.noyau } : s,
        ),
      };
    case 'promoteToAnalyzing':
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.id === action.id
            ? {
                ...s,
                text: action.text,
                noyau: action.noyau,
                status: 'analyzing' as const,
                jobId: null,
              }
            : s,
        ),
        openId: action.id,
      };
    case 'setJobId':
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.id === action.id ? { ...s, jobId: action.jobId, status: 'analyzing' } : s,
        ),
      };
    case 'open': {
      // Opening a ready session promotes it to "reviewing" (clears its badge).
      const sessions = state.sessions.map((s) =>
        s.id === action.id && s.status === 'ready' ? { ...s, status: 'reviewing' as const } : s,
      );
      return { ...state, sessions, openId: action.id };
    }
    case 'close':
      return { ...state, openId: null, screen: 'entry' };
    case 'beginReview':
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.id === action.id ? { ...s, status: 'reviewing' } : s,
        ),
      };
    case 'progress':
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.id === action.id ? { ...s, processed: action.processed, total: action.total } : s,
        ),
      };
    case 'ready':
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.id === action.id
            ? {
                ...s,
                status: 'ready',
                words: action.words,
                processed: action.words.length,
                total: action.words.length,
              }
            : s,
        ),
        toast: action.toast
          ? buildToast(state.sessions.find((s) => s.id === action.id))
          : state.toast,
      };
    case 'failAnalyze':
      return {
        ...state,
        sessions: state.sessions.map((s) => (s.id === action.id ? { ...s, status: 'error' } : s)),
      };
    case 'refreshMatches':
      // Re-analysis on open: refresh only the DB-derived fields. Preserve tags,
      // exclusions, and resolutions. A merge whose DB target vanished or changed
      // collapses back to a plain create (per the grilled spec).
      return mapSessionWords(state, action.id, (words) =>
        words.map((w) => {
          const fresh = action.matches.get(normalizeTitle(w.title)) ?? null;
          const op =
            w.op.kind === 'mergeInto' && fresh?.id !== w.op.targetId
              ? { kind: 'create' as const }
              : w.op;
          return { ...w, dbMatch: fresh, op };
        }),
      );
    case 'setTags':
      return mapSessionWords(state, action.id, (words) =>
        words.map((w) => (w.id === action.wordId ? { ...w, tags: action.tags } : w)),
      );
    case 'toggleExclude':
      return mapSessionWords(state, action.id, (words) =>
        words.map((w) => (w.id === action.wordId ? { ...w, excluded: !w.excluded } : w)),
      );
    case 'resolve':
      return applyResolution(state, action.id, action.resolution);
    case 'resolveAll':
      return applyGlobalResolution(state, action.id, action.kind);
    case 'restoreSnapshot':
      // Undo for the "apply to all" bar's global resolution: restores the exact
      // words + resolvedKeys captured just before that resolveAll ran.
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.id === action.id ? { ...s, words: action.words, resolvedKeys: action.resolvedKeys } : s,
        ),
      };
    case 'markFailed':
      // Drop the words that were written successfully so a retry only re-submits
      // the failures (re-submitting a created word would duplicate it). Keep the
      // failed rows (flagged) and any crossed-out rows (harmless, still excluded).
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.id === action.id
            ? {
                ...s,
                status: 'error',
                words: s.words
                  .filter((w) => w.excluded || action.failedIds.includes(w.id))
                  .map((w) => ({ ...w, failed: action.failedIds.includes(w.id) })),
              }
            : s,
        ),
      };
    case 'remove': {
      const sessions = state.sessions.filter((s) => s.id !== action.id);
      const openId = state.openId === action.id ? null : state.openId;
      const screen = openId === null ? 'entry' : state.screen;
      return { ...state, sessions, openId, screen };
    }
    case 'setWatching':
      return { ...state, watchingId: action.id };
    case 'dismissToast':
      return { ...state, toast: null };
  }
}

/** Build a completion toast from the session that just became ready. */
function buildToast(session: BulkSession | undefined): Toast | null {
  if (!session) return null;
  return { sessionId: session.id, name: session.name, noyau: session.noyau };
}

/** Apply a conflict resolution to one session's word list (pure). */
function applyResolution(state: State, id: string, resolution: BulkResolution): State {
  if (resolution.kind === 'keepBoth') {
    return {
      ...state,
      sessions: state.sessions.map((s) =>
        s.id === id ? { ...s, resolvedKeys: [...s.resolvedKeys, resolution.key] } : s,
      ),
    };
  }
  return mapSessionWords(state, id, (words, session) => {
    switch (resolution.kind) {
      case 'mergeBatch':
        // Keep one word, fold in the other's tags, bump the noyau weight (only
        // meaningful when the batch has a noyau), and drop the loser.
        return words
          .map((w) => {
            if (w.id !== resolution.keepId) return w;
            const drop = words.find((x) => x.id === resolution.dropId);
            return {
              ...w,
              tags: drop ? unionTags(w.tags, drop.tags) : w.tags,
              noyauWeight: session.noyau ? 2 : w.noyauWeight,
            };
          })
          .filter((w) => w.id !== resolution.dropId);
      case 'mergeDb':
        // Redirect the word to patch the existing DB original instead of creating
        // a new row; union its tags onto the original and bump the weight.
        return words.map((w) =>
          w.id === resolution.wordId
            ? {
                ...w,
                op: {
                  kind: 'mergeInto' as const,
                  targetId: resolution.dbId,
                  targetTitle: resolution.dbTitle,
                },
                tags: unionTags(w.tags, resolution.dbTags),
                noyauWeight: session.noyau ? 2 : w.noyauWeight,
              }
            : w,
        );
      case 'deleteOne':
        // "Supprimer l'un" reuses the reversible cross-out so it stays undoable.
        return words.map((w) => (w.id === resolution.wordId ? { ...w, excluded: true } : w));
    }
  });
}

/**
 * Apply one resolution kind to every currently unresolved conflict in a session
 * (the review footer's "apply to all" bar). Conflicts are walked in the same
 * order `detectConflicts` returns them; a conflict is skipped if one of its
 * words was already consumed (merged away / excluded) earlier in this same
 * pass, so overlapping pairs in a 3+-way duplicate group don't double-apply.
 * For batch-vs-batch "Supprimer l'un", the second occurrence in the pair is
 * always the one dropped (deterministic, no per-pair prompt).
 */
function applyGlobalResolution(state: State, id: string, kind: GlobalResolutionKind): State {
  return {
    ...state,
    sessions: state.sessions.map((s) => {
      if (s.id !== id) return s;
      const resolved = new Set(s.resolvedKeys);
      const conflicts = detectConflicts(s.words, resolved);
      if (conflicts.length === 0) return s;

      let words = s.words;
      const newResolvedKeys: string[] = [];
      const consumed = new Set<string>();

      for (const conflict of conflicts) {
        const idsInvolved =
          conflict.kind === 'batch' ? [conflict.a.id, conflict.b.id] : [conflict.word.id];
        if (idsInvolved.some((wordId) => consumed.has(wordId))) continue;

        if (kind === 'keepBoth') {
          newResolvedKeys.push(conflict.key);
          continue;
        }

        if (conflict.kind === 'batch') {
          if (kind === 'merge') {
            const dropId = conflict.b.id;
            words = words
              .map((w) =>
                w.id === conflict.a.id
                  ? {
                      ...w,
                      tags: unionTags(w.tags, conflict.b.tags),
                      noyauWeight: s.noyau ? 2 : w.noyauWeight,
                    }
                  : w,
              )
              .filter((w) => w.id !== dropId);
            consumed.add(dropId);
          } else {
            words = words.map((w) => (w.id === conflict.b.id ? { ...w, excluded: true } : w));
            consumed.add(conflict.b.id);
          }
        } else {
          if (kind === 'merge') {
            words = words.map((w) =>
              w.id === conflict.word.id
                ? {
                    ...w,
                    op: {
                      kind: 'mergeInto' as const,
                      targetId: conflict.dbWord.id,
                      targetTitle: conflict.dbWord.title,
                    },
                    tags: unionTags(w.tags, conflict.dbWord.tags),
                    noyauWeight: s.noyau ? 2 : w.noyauWeight,
                  }
                : w,
            );
          } else {
            words = words.map((w) => (w.id === conflict.word.id ? { ...w, excluded: true } : w));
          }
          consumed.add(conflict.word.id);
        }
      }

      return { ...s, words, resolvedKeys: [...s.resolvedKeys, ...newResolvedKeys] };
    }),
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Turn an analyzer result row into an in-memory candidate word. */
function makeBulkWord(analyzed: AnalyzedWord): BulkWord {
  return {
    id: crypto.randomUUID(),
    title: analyzed.title,
    tags: analyzed.tags,
    excluded: false,
    noyauWeight: 1,
    op: { kind: 'create' },
    dbMatch: analyzed.dbMatch,
    failed: false,
  };
}

/** Auto-name a session by its creation date, e.g. "Revue du 23/06/2026 14:30". */
function formatSessionName(date: Date): string {
  const d = date.toLocaleDateString('fr-FR');
  const t = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `Revue du ${d} ${t}`;
}

/** A simple awaitable delay used by the re-analysis poll loop. */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── Context ──────────────────────────────────────────────────────────────────

type BulkContext = {
  sessions: BulkSession[];
  openId: string | null;
  screen: 'entry' | 'input';
  toast: Toast | null;
  /** Number of ready-but-unopened sessions — drives the rail badge. */
  readyCount: number;
  /** Per-session autosave state (keyed by session id). */
  saveStatus: Record<string, SaveStatus>;
  getSession: (id: string) => BulkSession | undefined;
  startNew: () => void;
  cancelInput: () => void;
  analyze: (text: string, noyau: NoyauCode | null, draftId?: string | null) => Promise<void>;
  /**
   * Create (first call, `id` null) or update (subsequent calls) the in-progress
   * input-phase draft, so it's persisted before "Analyser" is ever clicked.
   * Returns the draft's id, to be threaded back on the next call and into
   * `analyze` so it promotes the same draft instead of creating a second one.
   */
  updateInputDraft: (id: string | null, text: string, noyau: NoyauCode | null) => string;
  openSession: (id: string) => void;
  beginReview: (id: string) => void;
  closeSession: () => void;
  closePending: () => void;
  cancelSession: (id: string) => void;
  finishSession: (id: string) => void;
  saveAndClose: (id: string) => Promise<void>;
  setWordTags: (id: string, wordId: string, tags: WordTag[]) => void;
  toggleExclude: (id: string, wordId: string) => void;
  resolve: (id: string, resolution: BulkResolution) => void;
  resolveAll: (id: string, kind: GlobalResolutionKind) => void;
  /** Undo for the "apply to all" bar: restores a caller-captured pre-resolution snapshot. */
  restoreSnapshot: (id: string, words: BulkWord[], resolvedKeys: string[]) => void;
  submit: (id: string) => Promise<SubmitSummary>;
  setWatching: (id: string | null) => void;
  dismissToast: () => void;
};

const BulkCtx = createContext<BulkContext | null>(null);

/** Provider holding all bulk-add session state + the analysis polling loop. */
export function BulkAddProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    sessions: [],
    openId: null,
    screen: 'entry',
    watchingId: null,
    toast: null,
  });
  const [saveStatus, setSaveStatus] = useState<Record<string, SaveStatus>>({});

  // Stable refs so the polling loop always sees current state without resubscribing.
  const stateRef = useRef(state);
  stateRef.current = state;
  const inflight = useRef(new Set<string>());
  // False after unmount, so a save that resolves late doesn't setState or schedule
  // a retry on a torn-down provider.
  const mountedRef = useRef(true);
  // One pending save timer (debounce or retry) per session.
  const saveTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // Sessions currently being re-analyzed on open (guards against overlap).
  const refreshing = useRef(new Set<string>());
  // How many times a dead analysis job has been self-healed (caps the retry).
  const healAttempts = useRef(new Map<string, number>());

  const hasAnalyzing = state.sessions.some((s) => s.status === 'analyzing' && s.jobId);

  // ── Autosave ───────────────────────────────────────────────────────────────
  // Write the whole session snapshot through to the server. On failure keep the
  // user editing (never block) and retry with backoff; a 401 routes to login.
  const saveSession = useCallback(async (session: BulkSession): Promise<void> => {
    setSaveStatus((s) => ({ ...s, [session.id]: 'saving' }));
    try {
      await putDraft(session);
      if (mountedRef.current) setSaveStatus((s) => ({ ...s, [session.id]: 'saved' }));
    } catch (err) {
      if (mountedRef.current) setSaveStatus((s) => ({ ...s, [session.id]: 'error' }));
      // Session expired → route to login; a permanent 4xx won't fix itself, so
      // don't retry either. Only transient failures (network / 5xx) are retried.
      if (err instanceof DraftUnauthorizedError) {
        notifyUnauthorized();
        return;
      }
      if (err instanceof DraftClientError) return;
      if (!mountedRef.current) return;
      const timer = setTimeout(() => {
        const latest = stateRef.current.sessions.find((s) => s.id === session.id);
        if (latest) void saveSession(latest);
      }, SAVE_RETRY_MS);
      saveTimers.current.set(session.id, timer);
    }
  }, []);

  // Debounced write-through: coalesce a burst of edits into one save.
  const scheduleSave = useCallback(
    (id: string): void => {
      const existing = saveTimers.current.get(id);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        const session = stateRef.current.sessions.find((s) => s.id === id);
        if (session) void saveSession(session);
      }, SAVE_DEBOUNCE_MS);
      saveTimers.current.set(id, timer);
    },
    [saveSession],
  );

  const clearSaveTimer = useCallback((id: string): void => {
    const existing = saveTimers.current.get(id);
    if (existing) clearTimeout(existing);
    saveTimers.current.delete(id);
  }, []);

  // ── Re-analysis on open ──────────────────────────────────────────────────────
  // Refresh each word's DB match against the live DB so a resumed draft reflects
  // current truth. Preserves the user's edits (see the reducer's refreshMatches).
  const refreshOnOpen = useCallback(
    async (id: string): Promise<void> => {
      const session = stateRef.current.sessions.find((s) => s.id === id);
      if (!session || refreshing.current.has(id)) return;
      if (!session.text || session.words.length === 0) return;
      refreshing.current.add(id);
      try {
        const jobId = await analyzeBulk(session.text, session.noyau);
        let job = await getBulkJob(jobId);
        for (let i = 0; i < MAX_REFRESH_POLLS && job.status === 'processing'; i++) {
          await delay(POLL_INTERVAL_MS);
          job = await getBulkJob(jobId);
        }
        if (job.status === 'done' && job.words) {
          const matches = new Map<string, DbMatch | null>();
          for (const w of job.words) matches.set(normalizeTitle(w.title), w.dbMatch);
          dispatch({ type: 'refreshMatches', id, matches });
          scheduleSave(id);
        }
      } catch {
        // Leave the (stale) matches in place — a failed refresh is non-fatal.
      } finally {
        refreshing.current.delete(id);
      }
    },
    [scheduleSave],
  );

  // ── Hydrate on mount ──────────────────────────────────────────────────────────
  // Server is the source of truth on load: pull the user's drafts once. A 401
  // routes to login; any other failure leaves the entry screen empty.
  useEffect(() => {
    let cancelled = false;
    listDrafts()
      .then((drafts) => {
        if (!cancelled) dispatch({ type: 'hydrate', sessions: drafts });
      })
      .catch((err) => {
        if (err instanceof DraftUnauthorizedError) notifyUnauthorized();
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Mark unmounted + clear every pending timer, so nothing writes state or retries
  // after teardown.
  useEffect(() => {
    const timers = saveTimers.current;
    return () => {
      mountedRef.current = false;
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  // Poll every analyzing session until its job reports done, then hydrate the
  // review list. Runs while the provider is mounted (i.e. anywhere under /words),
  // so analysis continues across navigation between destinations. A dead job
  // (e.g. after a backend restart / device switch) is self-healed once by
  // re-running the analysis from the persisted text.
  useEffect(() => {
    if (!hasAnalyzing) return;
    const tick = async (): Promise<void> => {
      for (const session of stateRef.current.sessions) {
        if (session.status !== 'analyzing' || !session.jobId) continue;
        if (inflight.current.has(session.id)) continue;
        inflight.current.add(session.id);
        try {
          const job = await getBulkJob(session.jobId);
          if (job.status === 'done' && job.words) {
            dispatch({
              type: 'ready',
              id: session.id,
              words: job.words.map(makeBulkWord),
              // No toast when the user is watching this analysis on screen — the
              // loading screen's button just enables instead.
              toast: stateRef.current.watchingId !== session.id,
            });
            scheduleSave(session.id);
          } else if (job.status === 'error') {
            dispatch({ type: 'failAnalyze', id: session.id });
          } else {
            dispatch({
              type: 'progress',
              id: session.id,
              processed: job.processed,
              total: job.total,
            });
          }
        } catch {
          // The job is gone (backend restart, or a draft resumed on another
          // device). Self-heal once by re-analyzing from the stored text; if that
          // also fails, mark the session errored.
          const current = stateRef.current.sessions.find((s) => s.id === session.id);
          const attempts = healAttempts.current.get(session.id) ?? 0;
          if (current?.text && attempts < 1) {
            healAttempts.current.set(session.id, attempts + 1);
            try {
              const jobId = await analyzeBulk(current.text, current.noyau);
              dispatch({ type: 'setJobId', id: session.id, jobId });
            } catch {
              dispatch({ type: 'failAnalyze', id: session.id });
            }
          } else {
            dispatch({ type: 'failAnalyze', id: session.id });
          }
        } finally {
          inflight.current.delete(session.id);
        }
      }
    };
    const handle = setInterval(() => void tick(), POLL_INTERVAL_MS);
    void tick();
    return () => clearInterval(handle);
  }, [hasAnalyzing, scheduleSave]);

  const getSession = useCallback(
    (id: string) => stateRef.current.sessions.find((s) => s.id === id),
    [],
  );

  // Create or update the in-progress input-phase draft (Point: draft available
  // from input phase). First call (id null) creates a new 'input' session;
  // subsequent calls update it in place. Debounce-saved like any other edit.
  const updateInputDraft = useCallback(
    (id: string | null, text: string, noyau: NoyauCode | null): string => {
      if (id === null) {
        const newId = crypto.randomUUID();
        const now = new Date().toISOString();
        const session: BulkSession = {
          id: newId,
          name: formatSessionName(new Date()),
          noyau,
          status: 'input',
          jobId: null,
          text,
          processed: 0,
          total: 0,
          words: [],
          resolvedKeys: [],
          createdAt: now,
          updatedAt: now,
        };
        dispatch({ type: 'createSession', session });
        scheduleSave(newId);
        return newId;
      }
      dispatch({ type: 'updateInputDraft', id, text, noyau });
      scheduleSave(id);
      return id;
    },
    [scheduleSave],
  );

  // Promote a draft to analyzing (or create a fresh session, when the input
  // phase never persisted one — e.g. a very fast paste-then-analyze), persist
  // it, kick off its analysis job, and open it (lands on loading).
  const analyze = useCallback(
    async (text: string, noyau: NoyauCode | null, draftId?: string | null): Promise<void> => {
      const now = new Date().toISOString();
      let session: BulkSession;
      if (draftId) {
        const existing = stateRef.current.sessions.find((s) => s.id === draftId);
        session = {
          id: draftId,
          name: existing?.name ?? formatSessionName(new Date()),
          noyau,
          status: 'analyzing',
          jobId: null,
          text,
          processed: 0,
          total: 0,
          words: [],
          resolvedKeys: existing?.resolvedKeys ?? [],
          createdAt: existing?.createdAt ?? now,
          updatedAt: now,
        };
        dispatch({ type: 'promoteToAnalyzing', id: draftId, text, noyau });
      } else {
        session = {
          id: crypto.randomUUID(),
          name: formatSessionName(new Date()),
          noyau,
          status: 'analyzing',
          jobId: null,
          text,
          processed: 0,
          total: 0,
          words: [],
          resolvedKeys: [],
          createdAt: now,
          updatedAt: now,
        };
        dispatch({ type: 'createSession', session });
      }
      void saveSession(session);
      try {
        const jobId = await analyzeBulk(text, noyau);
        // Attach the job id so the polling loop can pick the session up.
        dispatch({ type: 'setJobId', id: session.id, jobId });
      } catch {
        dispatch({ type: 'failAnalyze', id: session.id });
      }
    },
    [saveSession],
  );

  // Submit: create new words, patch DB merges, and report a summary. On full
  // success the session is left for the caller to finish (so the summary shows);
  // on partial failure the failed rows stay flagged and the session goes to error.
  const submit = useCallback(
    async (id: string): Promise<SubmitSummary> => {
      const session = stateRef.current.sessions.find((s) => s.id === id);
      if (!session) return { created: 0, merged: 0, failed: 0 };
      const active = session.words.filter((w) => !w.excluded);
      const toCreate = active.filter((w) => w.op.kind === 'create');
      const toMerge = active.filter((w) => w.op.kind === 'mergeInto');
      const failedIds: string[] = [];
      let created = 0;
      let merged = 0;

      for (const word of toCreate) {
        try {
          await wordsApi.create({
            title: word.title,
            noyauCode: session.noyau,
            tags: word.tags,
            noyauWeight: word.noyauWeight,
            imported: true,
          });
          created++;
        } catch {
          failedIds.push(word.id);
        }
      }
      for (const word of toMerge) {
        if (word.op.kind !== 'mergeInto') continue;
        try {
          const patch: WordPatch = { tags: word.tags };
          // With a batch noyau, a merge also bumps the existing word's weight for
          // that noyau by one — the same contribution a fresh create would have
          // seeded (see toTahitiCreateBody). Read the row to get its current
          // ponderation, then patch the incremented tuple; the patch diffs it down
          // to the single changed `ponderation_<code>` cell.
          if (session.noyau) {
            const idx = NOYAU_ORDER.findIndex((n) => n.code === session.noyau);
            if (idx !== -1) {
              const existing = await wordsApi.get(word.op.targetId);
              patch.noyauPonderation = incrementAt(existing.noyauPonderation, idx);
            }
          }
          await wordsApi.patch(word.op.targetId, patch);
          merged++;
        } catch {
          failedIds.push(word.id);
        }
      }
      if (failedIds.length > 0) {
        dispatch({ type: 'markFailed', id, failedIds });
        scheduleSave(id);
      }
      return { created, merged, failed: failedIds.length };
    },
    [scheduleSave],
  );

  // Stable identity: consumed by BulkLoading's mount/unmount effect, so it must
  // not change on every state update (which would re-run the effect each poll).
  const setWatching = useCallback((id: string | null) => dispatch({ type: 'setWatching', id }), []);

  // Drop back to the entry (cards) screen if the open session is only analyzing
  // or ready — i.e. still on the loading screen. Called when the bulk-add page
  // mounts, so returning to it while a job runs lands on the cards (the analysis
  // keeps polling in the background) instead of pinning the user to the loading
  // screen. A session being reviewed/errored is left open so the review resumes.
  // Stable (empty deps) + idempotent, so it's safe under StrictMode's double
  // effect invocation and only ever runs on a real page mount.
  const closePending = useCallback((): void => {
    const { openId, sessions } = stateRef.current;
    const open = openId ? sessions.find((s) => s.id === openId) : undefined;
    if (open && (open.status === 'analyzing' || open.status === 'ready')) {
      dispatch({ type: 'close' });
    }
  }, []);

  // Explicit "Enregistrer et quitter": flush the latest snapshot immediately,
  // then return to the entry screen.
  const saveAndClose = useCallback(
    async (id: string): Promise<void> => {
      clearSaveTimer(id);
      const session = stateRef.current.sessions.find((s) => s.id === id);
      if (session) await saveSession(session);
      dispatch({ type: 'close' });
    },
    [clearSaveTimer, saveSession],
  );

  // Remove a session both locally and server-side (Annuler + full-success submit).
  const removeSession = useCallback(
    (id: string): void => {
      clearSaveTimer(id);
      dispatch({ type: 'remove', id });
      void deleteDraft(id).catch(() => undefined);
    },
    [clearSaveTimer],
  );

  const value = useMemo<BulkContext>(
    () => ({
      sessions: state.sessions,
      openId: state.openId,
      screen: state.screen,
      toast: state.toast,
      readyCount: state.sessions.filter((s) => s.status === 'ready').length,
      saveStatus,
      getSession,
      startNew: () => dispatch({ type: 'startNew' }),
      cancelInput: () => dispatch({ type: 'cancelInput' }),
      analyze,
      updateInputDraft,
      openSession: (id) => {
        dispatch({ type: 'open', id });
        scheduleSave(id);
        void refreshOnOpen(id);
      },
      beginReview: (id) => {
        dispatch({ type: 'beginReview', id });
        scheduleSave(id);
      },
      closeSession: () => dispatch({ type: 'close' }),
      closePending,
      cancelSession: removeSession,
      finishSession: removeSession,
      saveAndClose,
      setWordTags: (id, wordId, tags) => {
        dispatch({ type: 'setTags', id, wordId, tags });
        scheduleSave(id);
      },
      toggleExclude: (id, wordId) => {
        dispatch({ type: 'toggleExclude', id, wordId });
        scheduleSave(id);
      },
      resolve: (id, resolution) => {
        dispatch({ type: 'resolve', id, resolution });
        scheduleSave(id);
      },
      resolveAll: (id, kind) => {
        dispatch({ type: 'resolveAll', id, kind });
        scheduleSave(id);
      },
      restoreSnapshot: (id, words, resolvedKeys) => {
        dispatch({ type: 'restoreSnapshot', id, words, resolvedKeys });
        scheduleSave(id);
      },
      submit,
      setWatching,
      dismissToast: () => dispatch({ type: 'dismissToast' }),
    }),
    [
      state,
      saveStatus,
      getSession,
      analyze,
      updateInputDraft,
      submit,
      setWatching,
      closePending,
      scheduleSave,
      refreshOnOpen,
      saveAndClose,
      removeSession,
    ],
  );

  return <BulkCtx.Provider value={value}>{children}</BulkCtx.Provider>;
}

/** Access the bulk-add context. Throws if used outside the provider. */
export function useBulkAdd(): BulkContext {
  const ctx = useContext(BulkCtx);
  if (!ctx) throw new Error('useBulkAdd must be used inside BulkAddProvider');
  return ctx;
}

/**
 * Read just the ready-but-unopened session count for the rail badge. Returns 0
 * when used outside the provider (so the rail renders standalone in tests),
 * rather than throwing like {@link useBulkAdd}.
 */
export function useBulkReadyCount(): number {
  return useContext(BulkCtx)?.readyCount ?? 0;
}
